/**
 * Utility functions for email thread display logic.
 *
 * Extracted from EmailViewModal for testability — determines when
 * to show chain/thread view, what count to display, etc.
 */

/**
 * Determine whether an email should attempt to load its thread chain.
 *
 * Previously the modal only fetched the chain when emailCount > 1.
 * Now we always fetch when an emailId is available, because:
 * - Sent replies may not be reflected in the list-level emailCount
 * - The chain API merges synced + sent emails for the complete picture
 *
 * @param emailId - The email UUID (null when modal is closed)
 * @returns true if the chain should be fetched
 */
export function shouldFetchChain(emailId: string | null | undefined): boolean {
  return !!emailId;
}

/**
 * Determine the display count for the thread badge in the header.
 *
 * Prefers the actual chain count (from the fetched chain) over the
 * list-level emailCount, because the chain includes sent replies
 * that aren't reflected in the list count.
 *
 * @param chainLength - Number of emails in the fetched chain (0 if not yet loaded)
 * @param listEmailCount - The email_count from the inbox list API
 * @returns The count to display, or null if no badge should show
 */
export function getThreadBadgeCount(
  chainLength: number,
  listEmailCount: number,
): number | null {
  // Show badge when chain has multiple messages (actual count)
  if (chainLength > 1) return chainLength;
  // Show badge from list count while chain is loading
  if (listEmailCount > 1) return listEmailCount;
  // No badge for single-email threads
  return null;
}

/**
 * Determine whether the Full Thread / Latest Only toggle should be visible.
 *
 * Only show when the chain actually contains multiple messages — there's
 * no point toggling between "full thread" and "latest only" when there's
 * only one message.
 *
 * @param chainLength - Number of emails in the fetched chain
 * @returns true if the toggle should be rendered
 */
export function shouldShowThreadToggle(chainLength: number): boolean {
  return chainLength > 1;
}

/**
 * Determine whether to render the chain thread view vs single email view.
 *
 * Thread view is the default — but only when the chain actually has
 * multiple messages. Single-email "chains" fall through to the
 * regular single-email body view.
 *
 * @param showChain - User's toggle preference (defaults to true)
 * @param chainLength - Number of emails in the fetched chain
 * @returns true if the chain thread view should be rendered
 */
export function shouldRenderThreadView(
  showChain: boolean,
  chainLength: number,
): boolean {
  return showChain && chainLength > 1;
}
